import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import * as _ from 'lodash';
import * as React from 'react';
import { Link } from 'react-router';
import {
  Configure,
  Highlight,
  InstantSearch,
  Snippet,
  useHits,
  useInstantSearch,
  useSearchBox
} from 'react-instantsearch';
import { SCROLL } from '../../scroll';
import { SearchFieldWidget } from '../../SearchFieldWidget';
import { BREAKPOINT, Z_INDEX } from '../../../vars';

const MAX_GROUPED_RESULTS = 8;
const RAW_HITS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

enum SearchIndexHitRecordType {
  DOCS = 'docs',
  BLOG = 'blog'
}

enum SearchIndexHitRecordKind {
  PAGE = 'page',
  SECTION = 'section'
}

interface SearchIndexHitRecord {
  objectID: string;
  type: SearchIndexHitRecordType;
  kind: SearchIndexHitRecordKind;
  url: string;
  title: string;
  sectionTitle?: string;
  description?: string;
  breadcrumb: string[];
  docsSection?: string;
  category?: string;
  date?: string;
  content: string;
}

type SearchIndexHit = SearchIndexHitRecord & {
  __position: number;
  __queryID?: string;
};

export interface AlgoliaSearchWidgetProps {
  className?: string;
  mobile?: boolean;
  onNavigate?: () => void;
}

const hasConfig = Boolean(__ALGOLIA_APP_ID__ && __ALGOLIA_SEARCH_API_KEY__ && __ALGOLIA_INDEX_NAME__);
const searchClient = hasConfig ? algoliasearch(__ALGOLIA_APP_ID__, __ALGOLIA_SEARCH_API_KEY__) : null;

export const AlgoliaSearchWidget: React.FC<AlgoliaSearchWidgetProps> = (props) => {
  const { className, mobile, onNavigate } = props;

  if (!searchClient) {
    return (
      <S.Root className={className} mobile={mobile}>
        <SearchFieldWidget
          aria-label="Search docs and blog"
          disabled
          value=""
          placeholder="Search unavailable"
          readOnly
        />
      </S.Root>
    );
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={__ALGOLIA_INDEX_NAME__}>
      <Configure hitsPerPage={RAW_HITS_PER_PAGE} />
      <SearchWidgetInner className={className} mobile={mobile} onNavigate={onNavigate} />
    </InstantSearch>
  );
};

interface SearchWidgetInnerProps extends AlgoliaSearchWidgetProps {}

const SearchWidgetInner: React.FC<SearchWidgetInnerProps> = (props) => {
  const { className, mobile, onNavigate } = props;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  return (
    <S.Root className={className} mobile={mobile} open={open} ref={rootRef}>
      <SearchInput onOpen={() => setOpen(true)} />
      <SearchResults
        open={open}
        onNavigate={() => {
          setOpen(false);
          onNavigate?.();
        }}
      />
    </S.Root>
  );
};

const SearchInput: React.FC<{ onOpen: () => void }> = (props) => {
  const { onOpen } = props;
  const { query, refine } = useSearchBox();
  const [inputValue, setInputValue] = React.useState(query);
  const debouncedRefine = React.useMemo(
    () =>
      _.debounce((value: string) => {
        refine(value);
      }, SEARCH_DEBOUNCE_MS),
    [refine]
  );
  const placeholder = useCompactHeaderSearchPlaceholder();

  React.useEffect(() => {
    setInputValue(query);
  }, [query]);

  React.useEffect(() => {
    return () => {
      debouncedRefine.cancel();
    };
  }, [debouncedRefine]);

  return (
    <SearchFieldWidget
      aria-label="Search docs and blog"
      onChange={(ev) => {
        const nextValue = ev.currentTarget.value;
        setInputValue(nextValue);

        if (!nextValue.trim()) {
          debouncedRefine.cancel();
          refine('');
        } else {
          debouncedRefine(nextValue);
        }

        onOpen();
      }}
      onFocus={onOpen}
      placeholder={placeholder}
      type="search"
      value={inputValue}
    />
  );
};

const useCompactHeaderSearchPlaceholder = () => {
  const [compact, setCompact] = React.useState(() => window.innerWidth <= BREAKPOINT.DOCS_SIDEBARS);

  React.useEffect(() => {
    const onResize = () => {
      setCompact(window.innerWidth <= BREAKPOINT.DOCS_SIDEBARS);
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return compact ? 'Search' : 'Search docs and blog';
};

const SearchResults: React.FC<{ open: boolean; onNavigate?: () => void }> = (props) => {
  const { open, onNavigate } = props;
  const { hits } = useHits<SearchIndexHit>();
  const { results, status } = useInstantSearch();
  const { query } = useSearchBox();
  const normalizedQuery = query.trim();
  const isLoading = status === 'loading' || status === 'stalled';

  if (!open || !normalizedQuery) {
    return null;
  }

  return (
    <S.Panel>
      {hits.length === 0 && !isLoading ? <S.EmptyState>No results for "{normalizedQuery}"</S.EmptyState> : null}
      {groupHitsByPage(getDistinctHits(hits))
        .slice(0, MAX_GROUPED_RESULTS)
        .map((group) => {
          const primaryHit = group.find((hit) => hit.kind === SearchIndexHitRecordKind.PAGE) ?? group[0];
          const childHits = group.filter((hit) => hit.kind === SearchIndexHitRecordKind.SECTION && hasChildMatch(hit));

          return (
            <S.HitGroup key={getPageUrl(primaryHit)}>
              <S.HitMeta>
                <S.HitType>{primaryHit.type === SearchIndexHitRecordType.DOCS ? 'Docs' : 'Blog'}</S.HitType>
                {primaryHit.type === SearchIndexHitRecordType.DOCS && primaryHit.docsSection ? (
                  <S.HitCategory>{primaryHit.docsSection}</S.HitCategory>
                ) : null}
                {primaryHit.type === SearchIndexHitRecordType.BLOG && primaryHit.category ? (
                  <S.HitCategory>{primaryHit.category}</S.HitCategory>
                ) : null}
                {getMetaBreadcrumb(primaryHit).length > 0 ? (
                  <S.HitMetaBreadcrumb>{getMetaBreadcrumb(primaryHit).join(' > ')}</S.HitMetaBreadcrumb>
                ) : null}
                {primaryHit.date ? <S.HitDate>{primaryHit.date.slice(0, 10)}</S.HitDate> : null}
              </S.HitMeta>
              <S.HitGroupTitleLink onClick={onNavigate} to={primaryHit.url}>
                <S.HitTitle>
                  <Highlight attribute="title" hit={primaryHit as any} />
                </S.HitTitle>
              </S.HitGroupTitleLink>
              {childHits.length > 0 ? (
                <S.HitEntries>
                  {childHits.map((hit) => (
                    <S.HitEntryLink key={hit.objectID} onClick={onNavigate} to={hit.url}>
                      <S.HitSectionTitle>
                        <Highlight attribute="sectionTitle" hit={hit as any} />
                      </S.HitSectionTitle>
                      <S.HitSnippet>
                        <Snippet attribute="content" hit={hit as any} />
                      </S.HitSnippet>
                    </S.HitEntryLink>
                  ))}
                </S.HitEntries>
              ) : null}
            </S.HitGroup>
          );
        })}
      {isLoading ? <S.StatusRow>Searching...</S.StatusRow> : null}
    </S.Panel>
  );
};

const getPageUrl = (hit: SearchIndexHit): string => hit.url.split('#')[0];

const getDistinctHits = (hits: SearchIndexHit[]): SearchIndexHit[] => {
  const seenPageUrls = new Set<string>();

  return hits.filter((hit) => {
    if (hit.type !== SearchIndexHitRecordType.BLOG) {
      return true;
    }

    const pageUrl = hit.url.split('#')[0];
    if (seenPageUrls.has(pageUrl)) {
      return false;
    }

    seenPageUrls.add(pageUrl);
    return true;
  });
};

const groupHitsByPage = (hits: SearchIndexHit[]): SearchIndexHit[][] => {
  const grouped = new Map<string, SearchIndexHit[]>();
  const orderedKeys: string[] = [];

  for (const hit of hits) {
    const pageUrl = getPageUrl(hit);
    if (!grouped.has(pageUrl)) {
      grouped.set(pageUrl, []);
      orderedKeys.push(pageUrl);
    }

    grouped.get(pageUrl)!.push(hit);
  }

  return orderedKeys.map((key) => grouped.get(key)!);
};

const hasHighlightMarkup = (value?: string): boolean => {
  return typeof value === 'string' && value.includes('<mark>');
};

const hasChildMatch = (hit: SearchIndexHit): boolean => {
  const algoliaHit = hit as SearchIndexHit & {
    _highlightResult?: { sectionTitle?: { value?: string } };
    _snippetResult?: { content?: { value?: string } };
  };

  return (
    hasHighlightMarkup(algoliaHit._highlightResult?.sectionTitle?.value) ||
    hasHighlightMarkup(algoliaHit._snippetResult?.content?.value)
  );
};

const getMetaBreadcrumb = (hit: SearchIndexHit): string[] => {
  if (hit.type === 'docs') {
    return hit.breadcrumb.slice(2, -1);
  }

  return hit.breadcrumb.slice(1, -1);
};

namespace S {
  const SEARCH_HIGHLIGHT = (theme: import('@emotion/react').Theme) => css`
    background: ${theme.header.searchHighlightBackground};
    color: ${theme.header.searchHighlightColor};
    border-radius: 6px;
    padding: 0 3px;
  `;

  export const Root = styled.div<{ mobile?: boolean; open?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    width: ${(p) => (p.mobile ? '100%' : p.open ? '440px' : '210px')};
    flex-shrink: 0;
    transition: width 180ms ease;

    @media (max-width: ${BREAKPOINT.DOCS_SIDEBARS}px) {
      width: ${(p) => (p.mobile ? '100%' : p.open ? '360px' : '120px')};
    }
  `;

  export const Panel = styled.div`
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    min-width: 100%;
    max-width: min(560px, calc(100vw - 32px));
    z-index: ${Z_INDEX.DESKTOP_DROPDOWN};
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: min(70dvh, 560px);
    overflow: auto;
    padding: 8px;
    border-radius: 18px;
    border: 1px solid ${(p) => p.theme.header.dropdownBorder};
    background: ${(p) => p.theme.header.dropdownBackground};
    box-shadow: ${(p) => p.theme.card.shadow};
    ${(p) => SCROLL(p.theme, false)};
  `;

  export const StatusRow = styled.div`
    padding: 8px 10px 4px;
    font-size: 12px;
    color: ${(p) => p.theme.header.searchPlaceholder};
  `;

  export const EmptyState = styled.div`
    padding: 8px 10px;
    font-size: 14px;
    color: ${(p) => p.theme.text.paragraph};
  `;

  export const HitLink = styled(Link)`
    text-decoration: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 13px;
    border-radius: 14px;
    color: ${(p) => p.theme.text.paragraph};

    &:hover {
      background: ${(p) => p.theme.header.dropdownHover};
    }
  `;

  export const HitGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 13px;
    border-radius: 14px;
  `;

  export const HitGroupTitleLink = styled(Link)`
    text-decoration: none;
    display: block;
    border-radius: 10px;
    padding: 2px 0;

    &:hover {
      background: ${(p) => p.theme.header.dropdownHover};
    }
  `;

  export const HitEntries = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;

  export const HitEntryLink = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-decoration: none;
    color: inherit;
    padding: 6px 8px;
    border-radius: 10px;

    &:hover {
      background: ${(p) => p.theme.header.dropdownHover};
      text-decoration: none;
    }
  `;

  export const HitMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 12px;
    color: ${(p) => p.theme.header.searchPlaceholder};
  `;

  export const HitType = styled.span`
    font-weight: 700;
    color: ${(p) => p.theme.text.special};
    flex-shrink: 0;
  `;

  export const HitCategory = styled.span`
    color: ${(p) => p.theme.text.paragraph};
    flex-shrink: 0;
  `;

  export const HitDate = styled.span`
    color: ${(p) => p.theme.header.searchPlaceholder};
    flex-shrink: 0;
  `;

  export const HitMetaBreadcrumb = styled.span`
    color: ${(p) => p.theme.header.searchPlaceholder};
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;

  export const HitTitle = styled.div`
    color: ${(p) => p.theme.text.heading};
    font-size: 15px;
    font-weight: 700;

    mark {
      ${(p) => SEARCH_HIGHLIGHT(p.theme)};
    }
  `;

  export const HitSectionTitle = styled.div`
    font-size: 13px;
    color: ${(p) => p.theme.header.searchPlaceholder};
    font-weight: 600;

    mark {
      ${(p) => SEARCH_HIGHLIGHT(p.theme)};
    }
  `;

  export const HitSnippet = styled.div`
    font-size: 13px;
    line-height: 1.5;
    color: ${(p) => p.theme.text.paragraph};

    mark {
      ${(p) => SEARCH_HIGHLIGHT(p.theme)};
    }
  `;
}
